import React from 'react';

import { createStyles, Divider, Group, LoadingOverlay, Paper, Text, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import PackageTypeLabel from 'components/PackageTypeLabel';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useAthletePackage from 'hooks/athletePackage/useAthletePackage';
import { AthletePackageRetrieve } from 'types';
import {
  formatAthletePackageValidityDisplay,
  getCreditDispensationsRemainingCredits,
  getCreditDispensationsTotalCredits,
} from 'utils/athlete-package.helpers';
import { parseDateTimeToDate } from 'utils/date-functions';

interface IPackageSummaryCardProps {
  athletePackage: AthletePackageRetrieve;
  setAthletePackageId: React.Dispatch<React.SetStateAction<string | undefined>>;
}

type AthletePackageHookData = ReturnType<typeof useAthletePackage>['data'];

const EARLY_RENEWAL_CAPTION = 'Renewed early: all credits used';

const useStyles = createStyles(theme => ({
  labelGrid: {
    display: 'grid',
    rowGap: theme.spacing.xs,
    columnGap: theme.spacing.md,
    alignItems: 'center',
    gridTemplateColumns: 'min-content auto',
    gridTemplateRows: 'auto',
    whiteSpace: 'nowrap',
    [theme.fn.smallerThan('sm')]: {
      fontSize: theme.fontSizes.sm,
    },
  },
  divider: {
    gridColumnStart: '1',
    gridColumnEnd: '3',
  },
}));

const renderPackageLink = (
  isSuccess: boolean,
  isLoading: boolean,
  packageData: AthletePackageHookData,
  onPackageClick: (id: string) => void,
  dateFormat: string,
  isEarlyRenewal: boolean,
  timezone?: string,
): JSX.Element => {
  if (isSuccess && packageData !== null && packageData !== undefined) {
    return (
      <>
        <Text
          weight='bold'
          underline
          sx={{ cursor: 'pointer', width: 'fit-content' }}
          onClick={() => onPackageClick(packageData.data.athletePackageId)}
        >
          {formatAthletePackageValidityDisplay(packageData.data, dateFormat, ' - ', timezone)}
        </Text>
        {isEarlyRenewal && (
          <Text c='dimmed' fz='xs' data-cy='earlyRenewalCaption'>
            {EARLY_RENEWAL_CAPTION}
          </Text>
        )}
      </>
    );
  } else if (isLoading) {
    return <LoadingOverlay visible={isLoading} />;
  }
  return <Text c='dimmed'>None</Text>;
};

const PackageSummaryCard = ({
  athletePackage,
  setAthletePackageId,
}: IPackageSummaryCardProps): JSX.Element => {
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);
  const { dateFormat } = useLocalization();
  const { session } = useAuth();

  const {
    data: previousPackage,
    isSuccess: isPreviousSuccess,
    isLoading: isPreviousLoading,
  } = useAthletePackage(athletePackage.parent?.athletePackageId);

  const {
    data: nextPackage,
    isSuccess: isNextSuccess,
    isLoading: isNextLoading,
  } = useAthletePackage(athletePackage.child?.athletePackageId);

  const getPreviousPackage = (): JSX.Element => {
    return renderPackageLink(
      isPreviousSuccess,
      isPreviousLoading,
      previousPackage,
      setAthletePackageId,
      dateFormat,
      athletePackage.renewalTrigger === 'CREDIT',
      session?.gym?.timezone,
    );
  };

  const getNextPackage = (): JSX.Element => {
    return renderPackageLink(
      isNextSuccess,
      isNextLoading,
      nextPackage,
      setAthletePackageId,
      dateFormat,
      nextPackage?.data.renewalTrigger === 'CREDIT',
      session?.gym?.timezone,
    );
  };

  const packageType = athletePackage.package.type;
  const showsEndDate = packageType === 'TIME_BASED' || packageType === 'TIME_OR_CREDIT_BASED';
  const showsCredits = packageType === 'CREDIT_BASED' || packageType === 'TIME_OR_CREDIT_BASED';

  return (
    <Paper px='lg' py='md' shadow='xs' radius='md' withBorder>
      <Text size={isDesktop ? 'lg' : 'md'} fw='bold' mb='xs'>
        Package
      </Text>
      <div className={classes.labelGrid}>
        <Text color='dimmed' fs='italic' fz='sm' ta='right'>
          Package Start Date
        </Text>
        <Text ta='left'>
          {dayjs(parseDateTimeToDate(athletePackage.startDate, session?.gym?.timezone)).format(dateFormat)}
        </Text>
        {athletePackage.cancellationDate && (
          <>
            <Text color='dimmed' fs='italic' fz='sm' ta='right'>
              Package New End Date
            </Text>
            <Text ta='left'>
              {dayjs(parseDateTimeToDate(athletePackage.cancellationDate, session?.gym?.timezone)).format(
                dateFormat,
              )}
            </Text>
          </>
        )}
        {showsEndDate && (
          <>
            <Text
              color='dimmed'
              fs='italic'
              fz='sm'
              ta='right'
              opacity={athletePackage.cancellationDate ? 0.7 : undefined}
            >
              {athletePackage.cancellationDate ? 'Package Original End Date' : 'Package End Date'}
            </Text>
            <Text
              ta='left'
              color={athletePackage.cancellationDate ? 'dimmed' : undefined}
              opacity={athletePackage.cancellationDate ? 0.7 : undefined}
            >
              {athletePackage.endDate &&
                dayjs(parseDateTimeToDate(athletePackage.endDate, session?.gym?.timezone)).format(
                  dateFormat,
                )}
            </Text>
          </>
        )}
        {showsCredits && (
          <>
            <Text color='dimmed' fs='italic' fz='sm' ta='right'>
              Credits Available
            </Text>
            <Text ta='left'>
              {getCreditDispensationsRemainingCredits(athletePackage.creditDispensations)}
              {' / '}
              {getCreditDispensationsTotalCredits(athletePackage.creditDispensations)} Credits
            </Text>
          </>
        )}
        <Divider mx='-md' className={classes.divider} />
        <Text color='dimmed' fs='italic' fz='sm' ta='right'>
          Auto Renew
        </Text>
        <Group spacing={4} noWrap>
          <Text>{athletePackage.autoRenew ? 'On' : 'Off'} |</Text>
          <PackageTypeLabel type={packageType} />
        </Group>
        <Text color='dimmed' fs='italic' fz='sm' ta='right'>
          Upcoming Package
        </Text>
        <Text ta='left'>{getNextPackage()}</Text>
        <Text color='dimmed' fs='italic' fz='sm' ta='right'>
          Previous Package
        </Text>
        <Text ta='left'>{getPreviousPackage()}</Text>
        <Divider mx='-md' className={classes.divider} />
        <Text color='dimmed' fs='italic' fz='sm' ta='right'>
          Status Upon Start
        </Text>
        <Text ta='left'>
          {athletePackage.athleteStatusOnStart ? athletePackage.athleteStatusOnStart : 'No Status Change'}
        </Text>
        <Text color='dimmed' fs='italic' fz='sm' ta='right'>
          {athletePackage.athleteStatusOnCancel ? 'Status Upon Cancelation' : 'Status Upon End'}
        </Text>
        <Text ta='left'>
          {athletePackage.athleteStatusOnCancel
            ? athletePackage.athleteStatusOnCancel
            : athletePackage.athleteStatusOnEnd
            ? athletePackage.athleteStatusOnEnd
            : 'No Status Change'}
        </Text>
      </div>
    </Paper>
  );
};

export default PackageSummaryCard;
